import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, FileCheck, Shield, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/hooks/useSession';
import { Button } from '@/components/ui/button';
import type {
  QmsChangeApprovalRecord,
  QmsChangeType,
  QmsTicket,
  QmsWorkflowAction,
} from '../../types/qms.types';
import { isPopulatedUser } from '../../types/qms.types';
import { QmsWorkflowSignatureDialog } from './QmsWorkflowSignatureDialog';

interface QmsChangeControlPanelProps {
  ticket: QmsTicket;
  onApprovalComplete: () => void;
}

export function QmsChangeControlPanel({
  ticket,
  onApprovalComplete,
}: QmsChangeControlPanelProps) {
  const { t } = useTranslation('qms');
  const { user } = useSession();
  const [pendingAction, setPendingAction] = useState<QmsWorkflowAction>();

  const canApproveChangeControl = user?.permission_strings?.includes('qmstickets.change_control')
    || user?.postgres_roles?.includes('full_admin')
    || user?.postgres_roles?.includes('director');
  const currentUserId = user?._id;
  const listedApprovers = ticket.change_approver_ids || [];
  const actorIsListed = listedApprovers.length === 0
    || (!!currentUserId && listedApprovers.includes(currentUserId));
  const changeApprovalStatus = ticket.change_approval_status
    ?? (ticket.change_approved === undefined ? undefined : ticket.change_approved ? 'approved' : 'rejected');

  const latestVotes = useMemo(() => {
    const votes = new Map<string, QmsChangeApprovalRecord>();
    for (const record of ticket.change_approval_records || []) votes.set(record.user_id, record);
    return votes;
  }, [ticket.change_approval_records]);
  const approvedCount = listedApprovers.filter((id) => latestVotes.get(id)?.decision === 'approved').length;

  if (!ticket.change_control_required) return null;

  const getChangeTypeBadgeClass = (type?: QmsChangeType) => {
    if (type === 'minor') return 'bg-blue-100 text-blue-800';
    if (type === 'major') return 'bg-orange-100 text-orange-800';
    if (type === 'critical') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (changeApprovalStatus === 'approved' || changeApprovalStatus === 'rejected') {
    const isApproved = changeApprovalStatus === 'approved';
    return (
      <div className="border-t pt-6">
        <div className={`rounded-lg border p-4 ${
          isApproved ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
        }`}>
          <div className="flex items-start gap-3">
            {isApproved
              ? <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
              : <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className={`font-medium ${isApproved ? 'text-green-800' : 'text-red-800'}`}>
                  {isApproved ? t('changeControlPanel.approved') : t('changeControlPanel.rejected')}
                </h4>
                {ticket.change_type && (
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${getChangeTypeBadgeClass(ticket.change_type)}`}>
                    {t(`changeControl.types.${ticket.change_type}`)}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-600">
                {listedApprovers.length > 0
                  ? `${approvedCount} of ${listedApprovers.length} listed CCB members approved`
                  : 'Signed change-control decision recorded'}
              </p>
              {isPopulatedUser(ticket.change_approved_by) && (
                <p className="mt-1 text-sm text-gray-600">
                  {t('changeControlPanel.approvedBy', {
                    name: `${ticket.change_approved_by.firstname} ${ticket.change_approved_by.lastname}`,
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border-t pt-6">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <FileCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-medium text-blue-800">{t('changeControlPanel.title')}</h4>
                <span className="inline-flex items-center gap-1 rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                  <Clock className="h-3 w-3" />
                  {t('changeControlPanel.pending')}
                </span>
                {ticket.change_type && (
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${getChangeTypeBadgeClass(ticket.change_type)}`}>
                    {t(`changeControl.types.${ticket.change_type}`)}
                  </span>
                )}
              </div>

              {listedApprovers.length > 0 && (
                <div className="mt-3 rounded-lg border border-blue-100 bg-white p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">CCB approval progress</span>
                    <span className="text-slate-600">{approvedCount}/{listedApprovers.length}</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full bg-teal-600"
                      style={{ width: `${listedApprovers.length ? (approvedCount / listedApprovers.length) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {ticket.change_impact_assessment && (
                <div className="mt-3 rounded border border-blue-100 bg-white p-3">
                  <p className="mb-1 text-xs font-medium text-gray-600">
                    {t('form.fields.changeImpactAssessment')}
                  </p>
                  <div
                    className="prose prose-sm max-w-none text-sm text-gray-700"
                    dangerouslySetInnerHTML={{ __html: ticket.change_impact_assessment }}
                  />
                </div>
              )}
              {ticket.training_applied && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-700">
                  <Shield className="h-4 w-4" />
                  {t('changeControlPanel.trainingApplied')}
                </div>
              )}

              {canApproveChangeControl && actorIsListed ? (
                <div className="mt-4 flex gap-2">
                  <Button type="button" size="sm" onClick={() => setPendingAction('approve_change')} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="mr-1.5 h-4 w-4" />
                    Sign approval
                  </Button>
                  <Button type="button" size="sm" variant="destructive" onClick={() => setPendingAction('reject_change')}>
                    <XCircle className="mr-1.5 h-4 w-4" />
                    Sign rejection
                  </Button>
                </div>
              ) : (
                <p className="mt-3 flex items-center gap-1.5 text-xs text-blue-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {actorIsListed
                    ? t('changeControlPanel.noPermission')
                    : 'Only a listed Change Control Board member can sign this decision.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {pendingAction && (
        <QmsWorkflowSignatureDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingAction(undefined);
          }}
          ticket={ticket}
          action={pendingAction}
          onCompleted={() => {
            setPendingAction(undefined);
            onApprovalComplete();
          }}
        />
      )}
    </>
  );
}
